import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Pencil, Plus, Trash2, Camera } from "lucide-react";
import MiniGridLoader from '@/components/MiniGridLoader';
import { toast } from "sonner";
import { format } from "date-fns";
import { Progress } from "./ui/progress";
import { getMetricsByCategory, getMetric, getAllMetrics } from "@/lib/metricDefinitions";
import CustomMetricsDialog from "./CustomMetricsDialog";
import { ReferralCreditsDialog } from "./ReferralCreditsDialog";
import { GitHubOAuth } from "./GitHubOAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn, getLocalizedMetricName } from "@/lib/utils";
import { hapticTap, hapticToggle } from "@/utils/haptics";
import {
  getAvailableUnits,
  convertFromBase,
  convertToBase,
  formatValueWithUnit,
  UnitType,
  getMetricBaseUnit
} from "@/lib/unitConversions";
import { getUnitPreference, setUnitPreference } from "@/lib/unitPreferences";
import { trackOperation } from "@/lib/undoTracking";
import { maybeRequestAppReview } from "@/lib/appReview";
import { isDemoAccount, DEMO_ACCOUNT_USER_ID } from "@/lib/screenshotDataGenerator";

interface QuickMetricButtonsProps {
  userId: string;
  selectedDate?: Date;
  layout?: 'horizontal' | 'grid';
  hideTutorialTooltips?: boolean;
  displayMode?: 'circles' | 'list';
  showAIBubble?: boolean;
  onUpdateComplete?: () => void; // Called after a metric update completes (useful for resetting date picker)
  username?: string; // For screenshot mode display
}

export const QuickMetricButtons = ({ userId, selectedDate = new Date(), layout = 'horizontal', hideTutorialTooltips = false, displayMode = 'circles', showAIBubble = false, onUpdateComplete, username }: QuickMetricButtonsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [selectedMetric, setSelectedMetric] = useState<{
    key: string;
    name: string;
    emoji: string;
    unit: string;
    currentValue: number | null;
    goal: number;
    isBoolean?: boolean;
    isCustom?: boolean;
    customMetricId?: string;
    readOnly?: boolean;
    isRelative?: boolean;
  } | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [booleanValue, setBooleanValue] = useState(false);
  const [isEditingGoal, setIsEditingGoal] = useState(false);
  const [goalValue, setGoalValue] = useState("");
  const [isCaloriesInverse, setIsCaloriesInverse] = useState(true);
  const [currentDisplayUnit, setCurrentDisplayUnit] = useState<UnitType | null>(null);
  const [flashingMetrics, setFlashingMetrics] = useState<Record<string, 'increase' | 'decrease'>>({});
  const prevValuesRef = useRef<Record<string, number | null>>({});
  const [newMetrics, setNewMetrics] = useState<Set<string>>(new Set());
  const prevMetricKeysRef = useRef<Set<string>>(new Set());
  const [showAddMetric, setShowAddMetric] = useState(false);
  const [showCustomMetricsDialog, setShowCustomMetricsDialog] = useState(false);
  const [showEditDetailsDialog, setShowEditDetailsDialog] = useState(false);
  const [metricKeyToDelete, setMetricKeyToDelete] = useState<string | null>(null);
  const [referralCreditsData, setReferralCreditsData] = useState<{
    creditsAwarded: number;
    referrerUsername: string;
    referrerName: string;
  } | null>(null);
  const [showSingleMetricTutorial, setShowSingleMetricTutorial] = useState(false);
  const [showNoMetricsTooltip, setShowNoMetricsTooltip] = useState(false);
  const [mobileBackdropActive, setMobileBackdropActive] = useState(false);
  const [showZoneTutorial, setShowZoneTutorial] = useState<{
    metricKey: string;
    type: 'numeric' | 'boolean';
  } | null>(null);
  const [hasSeenNumericTutorial, setHasSeenNumericTutorial] = useState(false);
  const [hasSeenBooleanTutorial, setHasSeenBooleanTutorial] = useState(false);
  const overlayTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [punchingMetrics, setPunchingMetrics] = useState<Set<string>>(new Set());
  const [floatingIndicators, setFloatingIndicators] = useState<Array<{
    id: string;
    metricKey: string;
    value: number;
    x: number;
    y: number;
  }>>([]);
  const queryClient = useQueryClient();
  const today = format(selectedDate, "yyyy-MM-dd");

  // Check if this is the demo account (by both username AND userId for extra safety)
  const isThisDemoAccount = isDemoAccount(username) && userId === DEMO_ACCOUNT_USER_ID;

  console.log('[QuickMetricButtons] Demo account check:', {
    username,
    userId,
    DEMO_ACCOUNT_USER_ID,
    isDemoAccountByUsername: isDemoAccount(username),
    isThisDemoAccount
  });

  // Load screenshot values from localStorage for demo account
  const screenshotValues = useMemo(() => {
    if (!isThisDemoAccount) {
      console.log('[QuickMetricButtons] Not demo account, skipping screenshot values');
      return null;
    }
    try {
      const stored = localStorage.getItem('gitfit-screenshot-values');
      console.log('[QuickMetricButtons] Screenshot values from localStorage:', stored ? 'found' : 'not found');
      if (stored) {
        const parsed = JSON.parse(stored);
        console.log('[QuickMetricButtons] Parsed screenshot values keys:', Object.keys(parsed));
        const result: Record<string, Map<string, number | boolean | null>> = {};
        for (const [metricKey, data] of Object.entries(parsed)) {
          result[metricKey] = new Map(Object.entries(data as Record<string, number | boolean | null>));
        }
        return result;
      }
    } catch (e) {
      console.error('Failed to load screenshot values from localStorage:', e);
    }
    return null;
  }, [isThisDemoAccount]);

  // Helper to get screenshot value for a metric on the selected date
  const getScreenshotValue = (metricKey: string): number | boolean | null | undefined => {
    if (!screenshotValues || !screenshotValues[metricKey]) return undefined;
    const value = screenshotValues[metricKey].get(today);
    console.log('[QuickMetricButtons] getScreenshotValue:', { metricKey, today, value });
    return value;
  };

  // Function to show floating +1/-1 indicator
  const showFloatingIndicator = (metricKey: string, value: number, clientX: number, clientY: number) => {
    const id = `${metricKey}-${Date.now()}`;
    setFloatingIndicators(prev => [...prev, { id, metricKey, value, x: clientX, y: clientY }]);

    // Remove after animation completes
    setTimeout(() => {
      setFloatingIndicators(prev => prev.filter(indicator => indicator.id !== id));
    }, 1000);
  };

  // Listen for mobile backdrop state changes from QuickEntryInput
  useEffect(() => {
    const handleBackdropState = (e: CustomEvent) => {
      setMobileBackdropActive(e.detail.active);
    };

    window.addEventListener('mobile-backdrop-state', handleBackdropState as EventListener);
    return () => window.removeEventListener('mobile-backdrop-state', handleBackdropState as EventListener);
  }, []);

  // Check if user has dismissed the manual metric tutorial for mobile Track page
  useEffect(() => {
    if (layout === 'grid' && isMobile) {
      const dismissed = localStorage.getItem('manual_metric_tutorial_dismissed');
      setShowSingleMetricTutorial(!dismissed);
    }
  }, [layout, isMobile]);

  // Check if user has seen zone tutorials
  useEffect(() => {
    const seenNumeric = localStorage.getItem('list_view_numeric_zone_tutorial_seen');
    const seenBoolean = localStorage.getItem('list_view_boolean_zone_tutorial_seen');
    setHasSeenNumericTutorial(!!seenNumeric);
    setHasSeenBooleanTutorial(!!seenBoolean);
  }, []);

  // Auto-hide zone overlay after brief display (timer reset on each press)
  useEffect(() => {
    if (showZoneTutorial) {
      // Clear existing timer if any
      if (overlayTimerRef.current) {
        clearTimeout(overlayTimerRef.current);
      }

      // Set new timer
      overlayTimerRef.current = setTimeout(() => {
        setShowZoneTutorial(null);
        overlayTimerRef.current = null;
      }, 1600); // Show for 1600ms

      return () => {
        if (overlayTimerRef.current) {
          clearTimeout(overlayTimerRef.current);
          overlayTimerRef.current = null;
        }
      };
    }
  }, [showZoneTutorial]);

  // Show "Use AI or manually setup" tooltip after 1 second delay with fade-in
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowNoMetricsTooltip(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Initialize display unit when metric is selected
  useEffect(() => {
    if (selectedMetric && !selectedMetric.isBoolean && !selectedMetric.isCustom) {
      const availableUnits = getAvailableUnits(selectedMetric.key);
      if (availableUnits.length > 0) {
        // Check if user has a saved preference
        const savedUnit = getUnitPreference(selectedMetric.key);
        if (savedUnit && availableUnits.includes(savedUnit)) {
          setCurrentDisplayUnit(savedUnit);
        } else {
          // Use base unit as default
          const baseUnit = getMetricBaseUnit(selectedMetric.key);
          setCurrentDisplayUnit(baseUnit);
        }
      } else {
        setCurrentDisplayUnit(null);
      }
    } else {
      setCurrentDisplayUnit(null);
    }
  }, [selectedMetric]);

  // Helper function to capture a frame from video
  const captureFrame = (video: HTMLVideoElement): Promise<string> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(video, 0, 0);
      canvas.toBlob((blob) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(blob!);
      }, 'image/jpeg', 0.92);
    });
  };

  // Helper function to open progress pic camera (fullscreen overlay)
  const openProgressCamera = async (metricKey?: string) => {
    let stream: MediaStream | null = null;
    let overlay: HTMLDivElement | null = null;
    let currentFacingMode: 'environment' | 'user' = 'environment';
    
    const cleanup = () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (overlay && overlay.parentNode) {
        document.body.removeChild(overlay);
      }
    };
    
    try {
      stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        } 
      });
      
      // Create overlay container
      overlay = document.createElement('div');
      overlay.style.position = 'fixed';
      overlay.style.top = '0';
      overlay.style.left = '0';
      overlay.style.width = '100%';
      overlay.style.height = '100%';
      overlay.style.zIndex = '9999';
      overlay.style.backgroundColor = 'black';

      // Add styles for scan animation
      const style = document.createElement('style');
      style.textContent = `
        @keyframes scanFlash {
          0% { top: 0%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        .scan-line-qm {
          position: absolute;
          left: 0;
          width: 100%;
          height: 2px;
          background: linear-gradient(90deg, transparent 5%, rgba(255,255,255,0.95) 30%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.95) 70%, transparent 95%);
          box-shadow: 0 0 8px rgba(255,255,255,0.6), 0 0 20px rgba(255,255,255,0.3);
          z-index: 10000;
          opacity: 0;
          top: 0%;
          pointer-events: none;
        }
        .scan-flash-qm {
          animation: scanFlash 200ms ease-out forwards;
        }
        @keyframes pulseDotQm { 0%,100%{opacity:1} 50%{opacity:0.4} }
        .rec-dot-qm { animation: pulseDotQm 1s ease-in-out infinite; }
        .pose-slider-qm { -webkit-appearance:none; appearance:none; background:rgba(255,255,255,0.3); border-radius:2px; outline:none; cursor:pointer; }
        .pose-slider-qm::-webkit-slider-thumb { -webkit-appearance:none; width:20px; height:20px; border-radius:50%; background:white; cursor:pointer; }
        .pose-slider-qm::-moz-range-thumb { width:20px; height:20px; border-radius:50%; background:white; cursor:pointer; border:none; }
        .pose-thumb-strip-qm::-webkit-scrollbar { display:none; }
      `;
      overlay.appendChild(style);

      // Create scan line element
      const scanLine = document.createElement('div');
      scanLine.className = 'scan-line-qm';
      overlay.appendChild(scanLine);
      
      // Create video element
      const video = document.createElement('video');
      video.srcObject = stream;
      video.autoplay = true;
      video.playsInline = true;
      video.muted = true;
      video.controls = false; // Explicitly disable controls
      video.disablePictureInPicture = true; // Prevent PiP controls
      video.setAttribute('disableRemotePlayback', ''); // Prevent cast controls
      video.setAttribute('x-webkit-airplay', 'deny'); // Prevent AirPlay controls on iOS
      video.style.width = '100%';
      video.style.height = '100%';
      video.style.objectFit = 'cover';
      video.style.opacity = '0';
      video.style.transition = 'opacity 0.3s ease-out';
      video.style.pointerEvents = 'none'; // Prevent tap interactions that trigger native controls

      video.play().catch(e => console.warn('Video play failed:', e));

      // Wait for video to load before showing
      video.onloadeddata = () => {
        video.style.opacity = '1';
        video.style.transform = currentFacingMode === 'user' ? 'scaleX(-1)' : 'scaleX(1)';
      };

      overlay.appendChild(video);
      document.body.appendChild(overlay);

      // === POSE MATCH OVERLAY ===
      const poseOverlayImg = document.createElement('img');
      poseOverlayImg.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;opacity:0.3;display:none;pointer-events:none;z-index:10000;';
      overlay.appendChild(poseOverlayImg);

      let poseMatchActive = false;
      let poseSnaps: Array<{ id: string; image_url: string; snap_date: string }> = [];
      let currentPoseOpacity = 0.3;

      // Pose match panel (thumbnails + opacity slider)
      const posePanel = document.createElement('div');
      posePanel.style.cssText = 'position:absolute;top:80px;left:0;right:0;display:none;flex-direction:column;align-items:center;gap:8px;z-index:10002;padding:10px 12px;';

      const thumbStrip = document.createElement('div');
      thumbStrip.className = 'pose-thumb-strip-qm';
      thumbStrip.style.cssText = 'display:flex;gap:8px;overflow-x:auto;max-width:100%;padding:4px;-webkit-overflow-scrolling:touch;scrollbar-width:none;';
      posePanel.appendChild(thumbStrip);

      const sliderRow = document.createElement('div');
      sliderRow.style.cssText = 'display:flex;align-items:center;gap:8px;width:90%;max-width:280px;';

      const eyeOffIcon = document.createElement('span');
      eyeOffIcon.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>';

      const opacitySlider = document.createElement('input');
      opacitySlider.type = 'range';
      opacitySlider.min = '5';
      opacitySlider.max = '80';
      opacitySlider.step = '5';
      opacitySlider.value = '30';
      opacitySlider.className = 'pose-slider-qm';
      opacitySlider.style.cssText = 'flex:1;height:4px;';

      const eyeOnIcon = document.createElement('span');
      eyeOnIcon.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';

      opacitySlider.oninput = () => {
        currentPoseOpacity = parseInt(opacitySlider.value) / 100;
        poseOverlayImg.style.opacity = String(currentPoseOpacity);
      };

      sliderRow.appendChild(eyeOffIcon);
      sliderRow.appendChild(opacitySlider);
      sliderRow.appendChild(eyeOnIcon);
      posePanel.appendChild(sliderRow);
      overlay.appendChild(posePanel);

      // Pose match toggle button (top-left)
      const poseMatchBtn = document.createElement('button');
      poseMatchBtn.innerHTML = '<div style="width:40px;height:40px;border-radius:50%;background:rgba(255,255,255,0.15);display:flex;align-items:center;justify-content:center;transition:background 0.2s;"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg></div>';
      poseMatchBtn.style.cssText = 'position:absolute;top:20px;left:20px;border:none;background:transparent;cursor:pointer;padding:0;z-index:10002;';

      const populatePoseThumbs = () => {
        thumbStrip.innerHTML = '';
        poseSnaps.forEach((snap, idx) => {
          const thumb = document.createElement('img');
          thumb.src = snap.image_url;
          thumb.style.cssText = 'width:48px;height:48px;border-radius:8px;object-fit:cover;cursor:pointer;flex-shrink:0;transition:border-color 0.2s;border:2px solid ' + (idx === 0 ? 'white' : 'transparent') + ';';
          thumb.onclick = () => {
            poseOverlayImg.src = snap.image_url;
            thumbStrip.querySelectorAll('img').forEach((t, i) => {
              (t as HTMLElement).style.borderColor = i === idx ? 'white' : 'transparent';
            });
          };
          thumbStrip.appendChild(thumb);
        });
      };

      poseMatchBtn.onclick = async () => {
        if (poseMatchActive) {
          poseMatchActive = false;
          poseOverlayImg.style.display = 'none';
          posePanel.style.display = 'none';
          (poseMatchBtn.firstElementChild as HTMLElement).style.background = 'rgba(255,255,255,0.15)';
          return;
        }

        if (poseSnaps.length === 0) {
          let q = supabase.from('progress_snaps').select('id, image_url, snap_date').eq('user_id', userId).not('image_url', 'is', null);
          if (metricKey) q = q.eq('metric_name', metricKey);
          const { data: snapData } = await q.order('snap_date', { ascending: false }).limit(20);
          if (!snapData || snapData.length === 0) {
            poseMatchBtn.style.transform = 'scale(1.2)';
            setTimeout(() => { poseMatchBtn.style.transform = 'scale(1)'; }, 200);
            return;
          }
          poseSnaps = snapData;
        }

        poseMatchActive = true;
        poseOverlayImg.src = poseSnaps[0].image_url;
        poseOverlayImg.style.opacity = String(currentPoseOpacity);
        poseOverlayImg.style.display = 'block';
        poseOverlayImg.style.transform = currentFacingMode === 'user' ? 'scaleX(-1)' : 'scaleX(1)';
        posePanel.style.display = 'flex';
        (poseMatchBtn.firstElementChild as HTMLElement).style.background = 'rgba(255,255,255,0.35)';
        populatePoseThumbs();
      };

      overlay.appendChild(poseMatchBtn);

      // Create UI container
      const uiContainer = document.createElement('div');
      uiContainer.style.position = 'absolute';
      uiContainer.style.bottom = '40px';
      uiContainer.style.left = '50%';
      uiContainer.style.transform = 'translateX(-50%)';
      uiContainer.style.display = 'flex';
      uiContainer.style.flexDirection = 'column';
      uiContainer.style.alignItems = 'center';
      uiContainer.style.gap = '16px';
      uiContainer.style.zIndex = '10001';

      // --- Hold-to-record button ---
      const MAX_RECORD_SECONDS = 10;
      const HOLD_THRESHOLD = 300;
      let holdTimer: ReturnType<typeof setTimeout> | null = null;
      let mediaRecorder: MediaRecorder | null = null;
      let recordedChunks: Blob[] = [];
      let isRecording = false;
      let recordStartTime = 0;
      let recordTimerInterval: ReturnType<typeof setInterval> | null = null;
      let autoStopTimer: ReturnType<typeof setTimeout> | null = null;

      const takePicBtn = document.createElement('button');
      takePicBtn.style.border = 'none';
      takePicBtn.style.background = 'transparent';
      takePicBtn.style.cursor = 'pointer';
      takePicBtn.style.padding = '0';
      takePicBtn.style.transition = 'transform 150ms ease-out';
      takePicBtn.style.position = 'relative';
      takePicBtn.style.width = '84px';
      takePicBtn.style.height = '84px';
      takePicBtn.style.touchAction = 'none';

      // SVG progress ring
      const ringSize = 84;
      const ringStroke = 4;
      const ringRadius = (ringSize - ringStroke) / 2;
      const ringCircumference = 2 * Math.PI * ringRadius;
      const ringSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      ringSvg.setAttribute('width', String(ringSize));
      ringSvg.setAttribute('height', String(ringSize));
      ringSvg.style.position = 'absolute';
      ringSvg.style.top = '0';
      ringSvg.style.left = '0';
      ringSvg.style.transform = 'rotate(-90deg)';
      const bgCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      bgCircle.setAttribute('cx', String(ringSize / 2));
      bgCircle.setAttribute('cy', String(ringSize / 2));
      bgCircle.setAttribute('r', String(ringRadius));
      bgCircle.setAttribute('fill', 'none');
      bgCircle.setAttribute('stroke', 'rgba(255,255,255,0.25)');
      bgCircle.setAttribute('stroke-width', String(ringStroke));
      ringSvg.appendChild(bgCircle);
      const progressCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      progressCircle.setAttribute('cx', String(ringSize / 2));
      progressCircle.setAttribute('cy', String(ringSize / 2));
      progressCircle.setAttribute('r', String(ringRadius));
      progressCircle.setAttribute('fill', 'none');
      progressCircle.setAttribute('stroke', '#ef4444');
      progressCircle.setAttribute('stroke-width', String(ringStroke));
      progressCircle.setAttribute('stroke-linecap', 'round');
      progressCircle.setAttribute('stroke-dasharray', String(ringCircumference));
      progressCircle.setAttribute('stroke-dashoffset', String(ringCircumference));
      progressCircle.style.transition = 'stroke-dashoffset 0.25s linear';
      ringSvg.appendChild(progressCircle);
      ringSvg.style.opacity = '0';
      takePicBtn.appendChild(ringSvg);

      const outerCircle = document.createElement('div');
      outerCircle.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:72px;height:72px;border-radius:50%;background:rgba(255,255,255,0.2);display:flex;align-items:center;justify-content:center;border:4px solid white;transition:border-color 0.2s;';
      const innerCircle = document.createElement('div');
      innerCircle.style.cssText = 'width:50px;height:50px;border-radius:50%;background:white;transition:all 0.2s ease;';
      outerCircle.appendChild(innerCircle);
      takePicBtn.appendChild(outerCircle);

      const timerDisplay = document.createElement('div');
      timerDisplay.style.cssText = 'position:absolute;top:-28px;left:50%;transform:translateX(-50%);color:white;font-size:14px;font-weight:600;display:none;align-items:center;gap:6px;white-space:nowrap;';
      const recDot = document.createElement('span');
      recDot.className = 'rec-dot-qm';
      recDot.style.cssText = 'width:8px;height:8px;border-radius:50%;background:#ef4444;display:inline-block;';
      const timerText = document.createElement('span');
      timerText.textContent = '0:00';
      timerDisplay.appendChild(recDot);
      timerDisplay.appendChild(timerText);
      takePicBtn.appendChild(timerDisplay);

      const hintLabel = document.createElement('div');
      hintLabel.textContent = 'Hold to record';
      hintLabel.style.cssText = 'color:rgba(255,255,255,0.6);font-size:11px;text-align:center;pointer-events:none;margin-top:4px;';

      const getRecordMime = (): string => {
        const types = ['video/mp4', 'video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
        for (const t of types) { if (MediaRecorder.isTypeSupported(t)) return t; }
        return '';
      };

      const startRecording = () => {
        const mimeType = getRecordMime();
        if (!mimeType || !stream) return;
        isRecording = true;
        recordedChunks = [];
        try {
          mediaRecorder = new MediaRecorder(stream, { mimeType });
        } catch {
          mediaRecorder = new MediaRecorder(stream);
        }
        mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) recordedChunks.push(e.data); };
        mediaRecorder.onstop = () => {
          if (recordedChunks.length === 0) return;
          const blob = new Blob(recordedChunks, { type: mediaRecorder?.mimeType || mimeType });
          const blobUrl = URL.createObjectURL(blob);
          (window as any).__pendingSnapVideoBlob = blob;
          cleanup();
          navigate('/create-snap', {
            state: {
              selectedDate,
              initialVideoUrl: blobUrl,
              selectedMetric: metricKey
            }
          });
        };
        mediaRecorder.start(100);
        recordStartTime = Date.now();

        ringSvg.style.opacity = '1';
        outerCircle.style.borderColor = '#ef4444';
        innerCircle.style.width = '24px';
        innerCircle.style.height = '24px';
        innerCircle.style.borderRadius = '6px';
        innerCircle.style.background = '#ef4444';
        timerDisplay.style.display = 'flex';
        hintLabel.style.display = 'none';

        recordTimerInterval = setInterval(() => {
          const elapsed = (Date.now() - recordStartTime) / 1000;
          const secs = Math.floor(elapsed);
          const frac = String(Math.floor((elapsed - secs) * 100)).padStart(2, '0');
          timerText.textContent = `${secs}:${frac}`;
          const progress = Math.min(elapsed / MAX_RECORD_SECONDS, 1);
          progressCircle.setAttribute('stroke-dashoffset', String(ringCircumference * (1 - progress)));
        }, 50);

        autoStopTimer = setTimeout(() => stopRecording(), MAX_RECORD_SECONDS * 1000);
      };

      const stopRecording = () => {
        if (autoStopTimer) { clearTimeout(autoStopTimer); autoStopTimer = null; }
        if (recordTimerInterval) { clearInterval(recordTimerInterval); recordTimerInterval = null; }
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
          mediaRecorder.stop();
        }
        isRecording = false;
      };

      const takePhoto = async () => {
        takePicBtn.style.pointerEvents = 'none';
        takePicBtn.style.opacity = '0.5';
        try {
          await new Promise(resolve => {
            if (video.readyState >= 2) resolve(null);
            else video.onloadeddata = () => resolve(null);
          });
          scanLine.classList.remove('scan-flash-qm');
          void scanLine.offsetWidth;
          scanLine.classList.add('scan-flash-qm');
          if (overlay) overlay.style.backgroundColor = 'rgba(255,255,255,0.3)';
          setTimeout(() => { if (overlay) overlay.style.backgroundColor = 'black'; }, 100);
          const frame = await captureFrame(video);
          cleanup();
          navigate('/create-snap', {
            state: { selectedDate, initialImage: frame, selectedMetric: metricKey }
          });
        } catch (error) {
          toast.error(t('notifications.toast.failed_to_capture_photo') + ' ' + (error instanceof Error ? error.message : 'Unknown error'));
          takePicBtn.style.pointerEvents = 'auto';
          takePicBtn.style.opacity = '1';
        }
      };

      takePicBtn.onpointerdown = (e) => {
        e.preventDefault();
        holdTimer = setTimeout(() => {
          holdTimer = null;
          startRecording();
        }, HOLD_THRESHOLD);
      };
      takePicBtn.onpointerup = (e) => {
        e.preventDefault();
        if (holdTimer) {
          clearTimeout(holdTimer);
          holdTimer = null;
          takePhoto();
        } else if (isRecording) {
          stopRecording();
        }
      };
      takePicBtn.onpointercancel = () => {
        if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
        if (isRecording) stopRecording();
      };
      takePicBtn.onpointerleave = () => {};

      // Close button
      const closeBtn = document.createElement('button');
      closeBtn.innerHTML = '✕';
      closeBtn.style.position = 'absolute';
      closeBtn.style.top = '20px';
      closeBtn.style.right = '20px';
      closeBtn.style.fontSize = '32px';
      closeBtn.style.color = 'white';
      closeBtn.style.background = 'rgba(0,0,0,0.5)';
      closeBtn.style.border = 'none';
      closeBtn.style.borderRadius = '50%';
      closeBtn.style.width = '50px';
      closeBtn.style.height = '50px';
      closeBtn.style.cursor = 'pointer';
      closeBtn.style.zIndex = '10002';
      
      closeBtn.onclick = () => {
        cleanup();
      };

      // Flip camera button
      const flipCameraBtn = document.createElement('button');
      flipCameraBtn.innerHTML = '<div style="width: 40px; height: 40px; border-radius: 50%; background: rgba(255,255,255,0.15); display: flex; align-items: center; justify-content: center;"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 19H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5"/><path d="M13 5h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-5"/><circle cx="12" cy="12" r="3"/><path d="m18 22-3-3 3-3"/><path d="m6 2 3 3-3 3"/></svg></div>';
      flipCameraBtn.style.position = 'absolute';
      flipCameraBtn.style.bottom = '20px';
      flipCameraBtn.style.right = '20px';
      flipCameraBtn.style.border = 'none';
      flipCameraBtn.style.background = 'transparent';
      flipCameraBtn.style.cursor = 'pointer';
      flipCameraBtn.style.padding = '0';
      flipCameraBtn.style.zIndex = '10002';

      flipCameraBtn.onclick = async () => {
        try {
          stream?.getTracks().forEach(track => track.stop());
          currentFacingMode = currentFacingMode === 'user' ? 'environment' : 'user';
          
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: { ideal: currentFacingMode },
              width: { ideal: 1920 },
              height: { ideal: 1080 }
            }
          });
          
          video.srcObject = stream;
          await video.play();
          
          // Mirror video when using front camera
          video.style.transform = currentFacingMode === 'user' ? 'scaleX(-1)' : 'scaleX(1)';
          if (poseMatchActive) {
            poseOverlayImg.style.transform = currentFacingMode === 'user' ? 'scaleX(-1)' : 'scaleX(1)';
          }
        } catch (error) {
          console.error('Failed to flip camera:', error);
          toast.error(t('notifications.toast.failed_to_flip_camera'));
        }
      };

      // Upload button (bottom left)
      const uploadBtn = document.createElement('button');
      uploadBtn.innerHTML = '<div style="width: 40px; height: 40px; border-radius: 50%; background: rgba(255,255,255,0.15); display: flex; align-items: center; justify-content: center;"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg></div>';
      uploadBtn.style.position = 'absolute';
      uploadBtn.style.bottom = '20px';
      uploadBtn.style.left = '20px';
      uploadBtn.style.border = 'none';
      uploadBtn.style.background = 'transparent';
      uploadBtn.style.cursor = 'pointer';
      uploadBtn.style.padding = '0';
      uploadBtn.style.zIndex = '10002';

      // Hidden file input for upload
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/*,video/*';
      fileInput.style.display = 'none';
      
      fileInput.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          if (file.type.startsWith('video/')) {
            const url = URL.createObjectURL(file);
            const tempVid = document.createElement('video');
            tempVid.preload = 'metadata';
            tempVid.src = url;
            try {
              await new Promise<void>((res, rej) => {
                tempVid.onloadedmetadata = () => res();
                tempVid.onerror = () => rej(new Error('Cannot read video'));
              });
              if (tempVid.duration > 10.5) {
                URL.revokeObjectURL(url);
                toast.error(t('notifications.toast.video_must_be_10_seconds_or_shorter'));
                return;
              }
            } catch {
              URL.revokeObjectURL(url);
              toast.error(t('notifications.toast.could_not_read_video_file'));
              return;
            }
            (window as any).__pendingSnapVideoBlob = file;
            cleanup();
            navigate('/create-snap', {
              state: { selectedDate, initialVideoUrl: url, selectedMetric: metricKey }
            });
          } else {
            const reader = new FileReader();
            reader.onload = () => {
              cleanup();
              navigate('/create-snap', {
                state: { selectedDate, initialImage: reader.result as string, selectedMetric: metricKey }
              });
            };
            reader.readAsDataURL(file);
          }
        }
      };
      
      uploadBtn.onclick = () => fileInput.click();

      uiContainer.appendChild(takePicBtn);
      uiContainer.appendChild(hintLabel);
      overlay.appendChild(uiContainer);
      overlay.appendChild(closeBtn);
      overlay.appendChild(flipCameraBtn);
      overlay.appendChild(uploadBtn);
      overlay.appendChild(fileInput);

    } catch (error) {
      toast.error(t('notifications.toast.failed_to_access_camera') + ' ' + (error instanceof Error ? error.message : 'Unknown error'));
      cleanup();
    }
  };

  // Fetch metric preferences
  const { data: metricPrefs, isPending: isMetricPrefsPending } = useQuery({
    queryKey: ["metric-preferences", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("metric_preferences")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
  });

  // Listen for realtime updates to metric preferences
  useEffect(() => {
    const channel = supabase
      .channel('metric-preferences-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'metric_preferences',
          filter: `user_id=eq.${userId}`
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["metric-preferences", userId] });
        }
      )
      .subscribe();

    return () => {
      // Unsubscribe and remove channel properly
      channel.unsubscribe().then(() => {
        supabase.removeChannel(channel);
      });
    };
  }, [userId, queryClient]);


  // Fetch custom metrics
  const { data: customMetrics } = useQuery({
    queryKey: ["custom-metrics", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("custom_metrics")
        .select("*")
        .eq("user_id", userId);
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch GitHub connection status
  const { data: githubConnection } = useQuery({
    queryKey: ["github-connection", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("github_connections")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
  });

  // Fetch user goals
  const { data: userGoals } = useQuery({
    queryKey: ["user-goals", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_goals")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
  });
  // Normalize legacy metric keys and category placement for consistency
  useEffect(() => {
    if (!metricPrefs) return;
    const normalize = (k: string) => {
      const map: Record<string, string> = {
        appearance: 'self_appearance_rating',
        miles: 'miles_ran',
        water: 'water_gallons',
        body_fat: 'body_fat_percentage',
      };
      return map[k] || k;
    };
    const uniq = (arr: string[]) => Array.from(new Set(arr));
    const norm = (arr?: string[]) => uniq((arr || []).map(normalize));
    let nutrition = norm(metricPrefs.nutrition_metrics);
    let activity = norm(metricPrefs.activity_sleep_metrics);
    let results = norm(metricPrefs.results_metrics);
    // Ensure water belongs to nutrition
    if (activity.includes('water_gallons')) {
      activity = activity.filter(k => k !== 'water_gallons');
      if (!nutrition.includes('water_gallons')) nutrition.push('water_gallons');
    }
    const changed =
      JSON.stringify(nutrition) !== JSON.stringify(metricPrefs.nutrition_metrics) ||
      JSON.stringify(activity) !== JSON.stringify(metricPrefs.activity_sleep_metrics) ||
      JSON.stringify(results) !== JSON.stringify(metricPrefs.results_metrics);
    if (changed) {
      const isFirstMetric = !metricPrefs || (
        (!metricPrefs.nutrition_metrics || metricPrefs.nutrition_metrics.length === 0) &&
        (!metricPrefs.activity_sleep_metrics || metricPrefs.activity_sleep_metrics.length === 0) &&
        (!metricPrefs.results_metrics || metricPrefs.results_metrics.length === 0)
      );

      supabase.from('metric_preferences').upsert({
        user_id: userId,
        nutrition_metrics: nutrition,
        activity_sleep_metrics: activity,
        results_metrics: results,
      }).then(async ({ error }) => {
        if (!error) {
          queryClient.invalidateQueries({ queryKey: ['metric-preferences', userId] });

          // Award referral credits on first metric creation
          if (isFirstMetric) {
            try {
              const { data: sessionData } = await supabase.auth.getSession();
              const token = sessionData.session?.access_token;
              if (!token) {
                console.warn('[ReferralCredits] No auth token when invoking award-referral-credits');
              } else {
                const { data: result, error: invokeError } = await supabase.functions.invoke('award-referral-credits', {
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (!invokeError && result?.success && result?.creditsAwarded) {
                  // Show celebratory dialog
                  setReferralCreditsData({
                    creditsAwarded: result.creditsAwarded,
                    referrerUsername: result.referrerUsername,
                    referrerName: result.referrerName
                  });
                  // Invalidate credits query to update display
                  queryClient.invalidateQueries({ queryKey: ['user-credits', userId] });
                }
              }
            } catch (err) {
              console.error('Error awarding referral credits:', err);
            }
          }
        } else {
          console.error('Failed to normalize metric preferences', error);
        }
      });
    }
  }, [metricPrefs, userId, queryClient]);

  // Catch-up: if user already has metrics enabled and referral pending, award credits
  useEffect(() => {
    if (!userId || !metricPrefs) return;
    const total = (metricPrefs.nutrition_metrics?.length || 0) + (metricPrefs.activity_sleep_metrics?.length || 0) + (metricPrefs.results_metrics?.length || 0);
    if (total > 0) {
      supabase
        .from('referrals')
        .select('id, credits_awarded, referred_user_first_metric_created')
        .eq('referred_user_id', userId)
        .eq('credits_awarded', false)
        .maybeSingle()
        .then(async ({ data, error }) => {
          if (!error && data && !data.referred_user_first_metric_created) {
            try {
              const { data: sessionData } = await supabase.auth.getSession();
              const token = sessionData.session?.access_token;
              if (!token) {
                console.warn('[ReferralCredits] No auth token when invoking award-referral-credits (catch-up)');
              } else {
                const { data: result, error: invokeError } = await supabase.functions.invoke('award-referral-credits', {
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (!invokeError && result?.success && result?.creditsAwarded) {
                  setReferralCreditsData({
                    creditsAwarded: result.creditsAwarded,
                    referrerUsername: result.referrerUsername,
                    referrerName: result.referrerName
                  });
                  queryClient.invalidateQueries({ queryKey: ['user-credits', userId] });
                }
              }
            } catch (err) {
              console.error('Catch-up referral award failed:', err);
            }
          }
        });
    }
  }, [userId, metricPrefs]);

  // Fetch today's entry
  const { data: todayEntry, refetch: refetchEntry, isPending: isTodayEntryPending } = useQuery({
    queryKey: ["daily-entry", userId, today],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("daily_entries")
        .select("*")
        .eq("user_id", userId)
        .eq("entry_date", today)
        .maybeSingle();
      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
  });

  // Update metric mutation (handles both numeric and boolean)
  const updateMetric = useMutation({
    mutationFn: async ({ metricKey, value, isBoolean }: { metricKey: string; value: number | boolean; isBoolean?: boolean }) => {
      // Convert custom metric keys to their column names in daily_entries
      let columnName = metricKey;
      if (metricKey.startsWith('custom_') && !metricKey.endsWith('_value')) {
        // Convert custom_1 to custom_1_value for daily_entries table
        columnName = `${metricKey}_value`;
      }

      // Convert boolean to number for database (0 or 1)
      const dbValue = isBoolean ? (value ? 1 : 0) : value;

      // Store previous value for undo
      const previousValue = todayEntry?.[columnName] ?? null;

      const { error } = await supabase
        .from("daily_entries")
        .upsert({
          user_id: userId,
          entry_date: today,
          [columnName]: dbValue,
        }, {
          onConflict: 'user_id,entry_date'
        });
      if (error) throw error;

      return { columnName, previousValue, newValue: dbValue as number };
    },
    onMutate: async ({ metricKey, value, isBoolean }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["daily-entry", userId, today] });

      // Snapshot previous value
      const previousEntry = queryClient.getQueryData(["daily-entry", userId, today]);

      // Convert custom metric keys
      let columnName = metricKey;
      if (metricKey.startsWith('custom_') && !metricKey.endsWith('_value')) {
        columnName = `${metricKey}_value`;
      }

      // Optimistically update cache
      const dbValue = isBoolean ? (value ? 1 : 0) : value;
      queryClient.setQueryData(["daily-entry", userId, today], (old: any) => {
        if (!old) {
          return {
            user_id: userId,
            entry_date: today,
            [columnName]: dbValue,
          };
        }
        return {
          ...old,
          [columnName]: dbValue,
        };
      });

      // Trigger punch animation
      setPunchingMetrics(prev => new Set(prev).add(metricKey));
      setTimeout(() => {
        setPunchingMetrics(prev => {
          const next = new Set(prev);
          next.delete(metricKey);
          return next;
        });
      }, 300);

      return { previousEntry };
    },
    onSuccess: (result, variables) => {
      refetchEntry();
      queryClient.invalidateQueries({ queryKey: ["daily-entries", userId] });
      // Also invalidate detailed-entries and daily-entry-total caches for the updated date/metric
      queryClient.invalidateQueries({ queryKey: ["detailed-entries", userId, today] });
      queryClient.invalidateQueries({ queryKey: ["daily-entry-total", userId, today] });
      // toast.success(t('notifications.toast.metric_updated')); // Removed - too noisy

      // Track operation for undo
      trackOperation({
        type: 'metric_value_update',
        userId,
        entryDate: today,
        metricUpdates: [{
          metricKey: result.columnName,
          previousValue: result.previousValue,
          newValue: result.newValue,
        }],
      });

      // Update the selected metric's current value immediately
      if (selectedMetric) {
        const newValue = typeof variables.value === 'boolean' ? (variables.value ? 1 : 0) : variables.value;
        setSelectedMetric({
          ...selectedMetric,
          currentValue: newValue
        });
      }

      // Close the dialog
      setSelectedMetric(null);
      setInputValue("");
      setBooleanValue(false);

      // Call the onUpdateComplete callback (e.g., to reset date picker to today)
      onUpdateComplete?.();

      // Request app review after successful metric update (only shown once)
      maybeRequestAppReview();
    },
    onError: (error: any, variables, context: any) => {
      // Rollback optimistic update
      if (context?.previousEntry) {
        queryClient.setQueryData(["daily-entry", userId, today], context.previousEntry);
      }
      console.error("Metric update error:", error);
      toast.error(t('quick_metrics.metric_update_failed'));
    },
  });

  // Update goal mutation
  const updateGoal = useMutation({
    mutationFn: async ({ goalKey, value, caloriesInverse }: { goalKey: string; value: number; caloriesInverse?: boolean }) => {
      // First, check if user_goals exists for this user
      const { data: existingGoals, error: selectError } = await supabase
        .from("user_goals")
        .select("id")
        .eq("user_id", userId)
        .maybeSingle();

      if (selectError) {
        console.error("Error checking existing goals:", selectError);
        throw selectError;
      }

      const updateData: Record<string, unknown> = { [goalKey]: value };
      // Include calories_inverse if updating calories goal
      if (goalKey === "calories_goal" && typeof caloriesInverse === "boolean") {
        updateData.calories_inverse = caloriesInverse;
      }

      if (existingGoals) {
        // Update existing goals
        const { error, data } = await supabase
          .from("user_goals")
          .update(updateData)
          .eq("user_id", userId)
          .select();

        console.log("Update result:", { error, data, goalKey, value });
        if (error) {
          console.error("Update error details:", error);
          throw error;
        }
      } else {
        // Insert new goals row
        const { error, data } = await supabase
          .from("user_goals")
          .insert({
            user_id: userId,
            ...updateData,
          })
          .select();

        console.log("Insert result:", { error, data, goalKey, value });
        if (error) {
          console.error("Insert error details:", error);
          throw error;
        }
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["user-goals", userId] });
      toast.success(t('quick_metrics.goal_updated'));
      setIsEditingGoal(false);

      // Update the selected metric's goal immediately
      if (selectedMetric) {
        setSelectedMetric({
          ...selectedMetric,
          goal: variables.value
        });
      }
    },
    onError: (error: any) => {
      console.error("Goal update error:", error);
      console.error("Error details:", JSON.stringify(error, null, 2));
      toast.error(t('quick_metrics.goal_update_failed'));
    },
  });

  // Load metric definitions from database
  const { data: nutritionMetrics = [], isPending: isNutritionPending } = useQuery({
    queryKey: ["metrics-nutrition"],
    queryFn: () => getMetricsByCategory('nutrition'),
  });

  const { data: activityMetrics = [], isPending: isActivityPending } = useQuery({
    queryKey: ["metrics-activity"],
    queryFn: () => getMetricsByCategory('activity_sleep'),
  });

  const { data: resultsMetrics = [], isPending: isResultsPending } = useQuery({
    queryKey: ["metrics-results"],
    queryFn: () => getMetricsByCategory('results'),
  });

  const { data: otherMetrics = [], isPending: isOtherPending } = useQuery({
    queryKey: ["metrics-other"],
    queryFn: () => getMetricsByCategory('other'),
  });

  const { data: healthkitMetrics = [], isPending: isHealthkitPending } = useQuery({
    queryKey: ["metrics-healthkit"],
    queryFn: () => getMetricsByCategory('healthkit'),
  });

  // Combined loading state for all metric-related queries
  const isMetricsLoading = isMetricPrefsPending || isTodayEntryPending || isNutritionPending || isActivityPending || isResultsPending || isOtherPending || isHealthkitPending;

  // Load saved metric order from user_chart_preferences (same as ContributionChart)
  const { data: savedMetricOrder } = useQuery({
    queryKey: ["chart-metric-order", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_chart_preferences")
        .select("active_metrics")
        .eq("user_id", userId)
        .maybeSingle();

      if (error && error.code !== "PGRST116") throw error;
      return data?.active_metrics || null;
    },
  });

  const metricDefinitions = {
    nutrition: nutritionMetrics,
    activity_sleep: activityMetrics,
    results: [...resultsMetrics, ...otherMetrics], // Include 'other' category metrics in results
    healthkit: healthkitMetrics,
  };

  const enabledMetrics = useMemo(() => {
    const metrics: Array<{
      key: string;
      name: string;
      emoji: string;
      unit: string;
      currentValue: number | null;
      goal: number;
      isBoolean?: boolean;
      isCustom?: boolean;
      customMetricId?: string;
      readOnly?: boolean;
      isInverse?: boolean;
      isRelative?: boolean;
    }> = [];

    // Helper to convert boolean values to numbers for consistent typing
    // Uses screenshot values if available for demo account
    const getValue = (key: string): number | null => {
      // Check for screenshot override first
      const screenshotVal = getScreenshotValue(key);
      if (screenshotVal !== undefined) {
        if (screenshotVal === null) return null;
        if (typeof screenshotVal === 'boolean') return screenshotVal ? 1 : 0;
        return Number(screenshotVal);
      }
      
      const rawValue = todayEntry?.[key];
      if (rawValue === null || rawValue === undefined) return null;
      if (typeof rawValue === 'boolean') return rawValue ? 1 : 0;
      return Number(rawValue);
    };

    // Start with no metrics enabled by default for new accounts
    const nutritionPrefs = metricPrefs?.nutrition_metrics ?? [];
    const activityPrefs = metricPrefs?.activity_sleep_metrics ?? [];
    const resultsPrefs = metricPrefs?.results_metrics ?? [];

    const resolveGoal = (def: any) => {
      const goalKey = def?.goalKey as string | undefined;
      if (!goalKey) return 10;
      const fromDb = (userGoals as any)?.[goalKey];
      // Use defaultGoal from metric definition if available
      return (fromDb ?? def?.defaultGoal ?? 10) as number;
    };

    const visibleCategories = ["nutrition", "activity_sleep", "results"];

    if (visibleCategories.includes("nutrition") && nutritionPrefs) {
      nutritionPrefs.forEach((key: string) => {
        const def = metricDefinitions.nutrition.find((m) => m.key === key);
        if (def) {
          const isBoolean = def.type === 'boolean';
          metrics.push({
            key: def.key,
            name: def.name,
            emoji: def.emoji,
            unit: def.unit,
            currentValue: getValue(def.key),
            goal: isBoolean ? 1 : resolveGoal(def),
            isBoolean,
            // For calories, use calories_inverse from userGoals; for added_sugars always true
            isInverse: def.key === "calories" 
              ? ((userGoals as any)?.calories_inverse !== false) 
              : def.key === "added_sugars" ? true : undefined,
          });
        }
      });
    }

    if (visibleCategories.includes("activity_sleep") && activityPrefs) {
      activityPrefs.forEach((key: string) => {
        // First try to find in activity_sleep category
        let def = metricDefinitions.activity_sleep.find((m) => m.key === key);

        // If not found and it's a HealthKit metric, look in healthkit category
        if (!def && key.startsWith('healthkit_')) {
          def = metricDefinitions.healthkit.find((m) => m.key === key);
        }

        if (def) {
          metrics.push({
            key: def.key,
            name: def.name,
            emoji: def.emoji,
            unit: def.unit,
            currentValue: getValue(def.key),
            goal: resolveGoal(def),
            readOnly: def.readOnly || def.isHealthKit || key === 'github_commits' || false,
          });
        }
      });
    }

    if (visibleCategories.includes("results") && resultsPrefs) {
      resultsPrefs.forEach((key: string) => {
        const def = metricDefinitions.results.find((m) => m.key === key);
        if (def) {
          metrics.push({
            key: def.key,
            name: def.name,
            emoji: def.emoji,
            unit: def.unit,
            currentValue: getValue(def.key),
            goal: resolveGoal(def),
            readOnly: def.readOnly || key === 'github_commits' || false,
          });
        }
      });
    }

    // Add custom metrics to quick edit circles
    if (customMetrics && customMetrics.length > 0) {
      customMetrics.forEach((metric, index) => {
        // Extract the slot number from metric_key (e.g., "custom_6" -> "6")
        const slotMatch = metric.metric_key.match(/^custom_(\d+)$/);
        const slotNumber = slotMatch ? slotMatch[1] : (index + 1).toString();
        const columnKey = `custom_${slotNumber}_value`;
        const goalKey = `custom_${slotNumber}_goal`;

        const isBoolean = (metric as any).metric_type === "boolean";
        const isRelative = (metric as any).is_relative || false;
        const rawValue = todayEntry?.[columnKey];
        const currentValue = getValue(columnKey);

        // Relative metrics (rating metrics) don't have goals, boolean metrics default to 1, others use stored goal or 100
        const retrievedGoal = isRelative ? null : (isBoolean ? 1 : (userGoals ? ((userGoals as any)[goalKey] || 100) : 100));

        console.log('[QuickMetricButtons] Custom Metric Debug:', {
          metricName: metric.metric_name,
          metricKey: metric.metric_key,
          columnKey,
          goalKey,
          retrievedGoal,
          isRelative,
          userGoalsObject: userGoals ? Object.keys(userGoals).filter(k => k.includes('custom')) : 'no userGoals',
          metricType: (metric as any).metric_type,
          isBoolean,
          rawValue,
          currentValue,
          todayEntry: todayEntry ? 'exists' : 'null'
        });

        metrics.push({
          key: metric.metric_key,
          name: metric.metric_name,
          emoji: metric.metric_emoji,
          unit: metric.metric_unit,
          currentValue: currentValue,
          goal: retrievedGoal,
          isCustom: true,
          customMetricId: metric.id,
          isInverse: (metric as any).is_inverse || false,
          isBoolean: isBoolean,
          isRelative: isRelative,
        });
      });
    }

    // Apply saved metric order if it exists (same logic as ContributionChart)
    if (savedMetricOrder && Array.isArray(savedMetricOrder) && savedMetricOrder.length > 0) {
      const metricsByKey = new Map(metrics.map(m => [m.key, m]));
      const orderedMetrics: typeof metrics = [];

      // Add metrics in saved order
      savedMetricOrder.forEach((key: string) => {
        const metric = metricsByKey.get(key);
        if (metric) {
          orderedMetrics.push(metric);
          metricsByKey.delete(key);
        }
      });

      // Add any remaining metrics that weren't in the saved order (e.g., newly added metrics)
      metricsByKey.forEach(metric => {
        orderedMetrics.push(metric);
      });

      return orderedMetrics;
    }

    return metrics;
  }, [metricPrefs, todayEntry, userGoals, customMetrics, metricDefinitions.nutrition, metricDefinitions.activity_sleep, metricDefinitions.results, metricDefinitions.healthkit, savedMetricOrder, screenshotValues, today]);

  // Listen for open-quick-edit events from ContributionChart
  useEffect(() => {
    const handleOpenQuickEdit = (event: CustomEvent) => {
      const { metricKey } = event.detail;

      // Find the metric in enabledMetrics
      const metric = enabledMetrics.find(m => m.key === metricKey);

      if (metric) {
        setSelectedMetric(metric);
      }
    };

    window.addEventListener('open-quick-edit', handleOpenQuickEdit as EventListener);

    return () => {
      window.removeEventListener('open-quick-edit', handleOpenQuickEdit as EventListener);
    };
  }, [enabledMetrics]);

  // Single metric tutorial bubble positioning
  const singleCircleRef = useRef<HTMLDivElement | null>(null);
  const [singleTipPos, setSingleTipPos] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    if (enabledMetrics.length !== 1) { setSingleTipPos(null); return; }
    const update = () => {
      const el = singleCircleRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setSingleTipPos({ top: rect.top - 48, left: rect.left, width: rect.width });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [enabledMetrics.length]);

  // Get available metrics (not currently enabled)
  const getAvailableMetrics = () => {
    const enabledKeys = new Set(enabledMetrics.map(m => m.key));
    const available: Array<{ key: string; name: string; emoji: string; category: string }> = [];

    const allMetrics = [
      ...metricDefinitions.nutrition.map(m => ({ ...m, category: 'nutrition' })),
      ...metricDefinitions.activity_sleep.map(m => ({ ...m, category: 'activity_sleep' })),
      ...metricDefinitions.results.map(m => ({ ...m, category: 'results' })),
      // Don't include healthkit metrics - they're synced automatically
    ];

    allMetrics.forEach(metric => {
      if (!enabledKeys.has(metric.key)) {
        available.push({
          key: metric.key,
          name: metric.name,
          emoji: metric.emoji,
          category: metric.category,
        });
      }
    });

    // Sort alphabetically, but keep GitHub Commits at the end
    return available.sort((a, b) => {
      if (a.key === 'github_commits') return 1;
      if (b.key === 'github_commits') return -1;
      return a.name.localeCompare(b.name);
    });
  };

  const addMetricMutation = useMutation({
    mutationFn: async ({ metricKey, category }: { metricKey: string; category: string }) => {
      const categoryFieldMap: Record<string, string> = {
        nutrition: 'nutrition_metrics',
        activity_sleep: 'activity_sleep_metrics',
        results: 'results_metrics',
        healthkit: 'activity_sleep_metrics', // HealthKit metrics are stored in activity_sleep_metrics
      };

      const field = categoryFieldMap[category];
      if (!field) throw new Error('Invalid category');

      const currentMetrics = metricPrefs?.[field] || [];

      const { error } = await supabase
        .from('metric_preferences')
        .upsert({
          user_id: userId,
          [field]: [...currentMetrics, metricKey],
        });

      if (error) throw error;
    },
    onSuccess: async () => {
      const hadNoMetrics = ((metricPrefs?.nutrition_metrics?.length ?? 0) + (metricPrefs?.activity_sleep_metrics?.length ?? 0) + (metricPrefs?.results_metrics?.length ?? 0)) === 0;
      queryClient.invalidateQueries({ queryKey: ["metric-preferences", userId] });
      setShowAddMetric(false);
      if (hadNoMetrics) {
        try {
          const { data: sessionData } = await supabase.auth.getSession();
          const token = sessionData.session?.access_token;
          if (!token) {
            console.warn('[ReferralCredits] No auth token when invoking award-referral-credits (addMetric)');
          } else {
            await supabase.functions.invoke('award-referral-credits', {
              headers: { Authorization: `Bearer ${token}` },
            });
          }
        } catch (err) {
          console.error('Failed to trigger referral credits:', err);
        }
      }
    },
    onError: () => {
      toast.error(t('notifications.toast.failed_to_enable_metric'));
    },
  });

  const findCategoryForKey = (key: string): 'nutrition' | 'activity_sleep' | 'results' | 'healthkit' | null => {
    if (metricDefinitions.nutrition.some(m => m.key === key)) return 'nutrition';
    if (metricDefinitions.activity_sleep.some(m => m.key === key)) return 'activity_sleep';
    if (metricDefinitions.results.some(m => m.key === key)) return 'results';
    if (metricDefinitions.healthkit.some(m => m.key === key)) return 'healthkit';
    return null;
  };

  const removeMetricMutation = useMutation({
    mutationFn: async ({ metricKey }: { metricKey: string }) => {
      // Handle custom metric deletion directly
      if (metricKey.startsWith('custom_')) {
        const { error } = await supabase
          .from("custom_metrics")
          .delete()
          .eq("user_id", userId)
          .eq("metric_key", metricKey);
        if (error) throw error;
        return;
      }
      const normalize = (k: string) => ({
        appearance: 'self_appearance_rating',
        miles: 'miles_ran',
        water: 'water_gallons',
        body_fat: 'body_fat_percentage',
      } as Record<string, string>)[k] || k;
      const normalizedKey = normalize(metricKey);
      const category = findCategoryForKey(normalizedKey);
      const fieldByCat: Record<string, string> = {
        nutrition: 'nutrition_metrics',
        activity_sleep: 'activity_sleep_metrics',
        results: 'results_metrics',
      };
      let field = category ? fieldByCat[category] : undefined;
      if (!field) {
        if (metricPrefs?.nutrition_metrics?.some((k: string) => normalize(k) === normalizedKey)) field = 'nutrition_metrics';
        else if (metricPrefs?.activity_sleep_metrics?.some((k: string) => normalize(k) === normalizedKey)) field = 'activity_sleep_metrics';
        else if (metricPrefs?.results_metrics?.some((k: string) => normalize(k) === normalizedKey)) field = 'results_metrics';
      }
      if (!field) throw new Error('unknown-metric');
      const current = (metricPrefs?.[field] as string[]) || [];
      const updated = current.filter((k: string) => normalize(k) !== normalizedKey);
      const { error } = await supabase.from('metric_preferences').upsert({
        user_id: userId,
        [field]: updated,
      });
      if (error) throw error;

      // Delete any penalties associated with this built-in metric
      const { error: penaltyError } = await supabase.rpc('delete_penalties_for_builtin_metric', {
        p_user_id: userId,
        p_metric_key: normalizedKey
      });

      if (penaltyError) {
        console.error('Failed to delete penalties for metric:', penaltyError);
        // Don't throw - metric was removed successfully, penalty deletion is secondary
      }
    },
    onSuccess: (_data, variables) => {
      if (variables.metricKey.startsWith('custom_')) {
        queryClient.invalidateQueries({ queryKey: ["custom-metrics", userId] });
      }
      queryClient.invalidateQueries({ queryKey: ["metric-preferences", userId] });
      queryClient.invalidateQueries({ queryKey: ["penalties"] });
      setSelectedMetric(null);
    },
    onError: () => {
      toast.error(t('notifications.toast.failed_to_remove_metric'));
    }
  });

  // Mutation to update custom metric details
  const updateCustomMetricDetails = useMutation({
    mutationFn: async (params: {
      metricKey: string;
      name: string;
      emoji: string;
      unit: string;
      goal: string;
      inverse: boolean;
      metricType: "numeric" | "boolean";
      isAdditive?: boolean;
      isRelative?: boolean;
      previousType?: "numeric" | "boolean";
    }) => {
      const { metricKey, name, emoji, unit, goal, inverse, metricType, isAdditive, isRelative, previousType } = params;

      // Update the custom metric in the database
      const { error: updateError } = await supabase
        .from("custom_metrics")
        .update({
          metric_name: name,
          metric_emoji: emoji,
          metric_unit: unit,
          is_inverse: inverse,
          metric_type: metricType,
          is_additive: isAdditive ?? true,
          is_relative: isRelative ?? false,
        })
        .eq("user_id", userId)
        .eq("metric_key", metricKey);

      if (updateError) throw updateError;

      // Update the goal if it's a numeric, non-relative metric
      if (metricType === "numeric" && !isRelative) {
        const goalValue = parseFloat(goal);
        if (!isNaN(goalValue) && goalValue >= 0) {
          const goalKey = `${metricKey}_goal`;
          const { error: goalError } = await supabase
            .from("user_goals")
            .upsert(
              {
                user_id: userId,
                [goalKey]: goalValue,
              },
              {
                onConflict: "user_id",
              }
            );
          if (goalError) throw goalError;
        }
      }

      // If the type changed, we need to handle the existing value
      const typeChanged = previousType && previousType !== metricType;
      if (typeChanged) {
        // Clear the value in daily_entries for today (and potentially reset it)
        const columnName = `${metricKey}_value`;
        const { error: clearError } = await supabase
          .from("daily_entries")
          .update({ [columnName]: null })
          .eq("user_id", userId)
          .eq("entry_date", today);

        // It's okay if there's no entry to clear
        if (clearError && clearError.code !== 'PGRST116') {
          console.warn("Could not clear old value:", clearError);
        }
      }

      return { typeChanged, metricKey, name, emoji, unit, metricType, isRelative };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["custom-metrics", userId] });
      queryClient.invalidateQueries({ queryKey: ["user-goals", userId] });
      if (result.typeChanged) {
        queryClient.invalidateQueries({ queryKey: ["daily-entry", userId, today] });
      }
      toast.success(t('components.custom_metrics_dialog.updated_notif'));
      setShowEditDetailsDialog(false);

      // Update the selectedMetric state with new values
      if (selectedMetric) {
        const parsedGoal = parseFloat(result.metricType === "boolean" || result.isRelative ? "1" : "0");
        setSelectedMetric({
          ...selectedMetric,
          name: result.name,
          emoji: result.emoji,
          unit: result.unit,
          isBoolean: result.metricType === "boolean",
          isRelative: result.isRelative,
          goal: isNaN(parsedGoal) ? selectedMetric.goal : parsedGoal,
          // Reset current value if type changed
          currentValue: result.typeChanged ? null : selectedMetric.currentValue,
        });
      }
    },
    onError: (error: any) => {
      console.error("Failed to update custom metric:", error);
      toast.error(t('notifications.toast.update_failed'));
    },
  });

  const getProgressColor = (current: number | null, goal: number, isBoolean?: boolean, metricKey?: string, isInverse?: boolean) => {
    if (isBoolean) {
      // For boolean metrics: if inverse, red when true, green when false
      // Otherwise, green when true, red when false
      if (isInverse) {
        return current === 1 ? "rgb(239, 68, 68)" : "rgb(34, 197, 94)";
      }
      return current === 1 ? "rgb(34, 197, 94)" : "rgb(239, 68, 68)";
    }
    if (!current || !goal) return "rgb(239, 68, 68)"; // red
    const percentage = (current / goal) * 100;

    // For inverse metrics (isInverse = true): over goal is bad (red)
    if (isInverse === true && percentage > 100) {
      const overshoot = percentage - 100;
      // Map overshoot to red intensity levels
      if (overshoot >= 50) return "rgb(220, 38, 38)"; // 50%+ over = dark red
      if (overshoot >= 25) return "rgb(239, 68, 68)"; // 25-50% over = red
      if (overshoot >= 10) return "rgb(248, 113, 113)"; // 10-25% over = light red
      return "rgb(252, 165, 165)"; // 0-10% over = very light red
    }
    
    // For non-inverse (isInverse = false): no red penalty, just normal progress colors

    if (percentage >= 100) return "rgb(34, 197, 94)"; // green
    if (percentage >= 75) return "rgb(132, 204, 22)"; // lime
    if (percentage >= 50) return "rgb(234, 179, 8)"; // yellow
    if (percentage >= 25) return "rgb(249, 115, 22)"; // orange
    return "rgb(239, 68, 68)"; // red
  };

  const handleSubmit = () => {
    if (!selectedMetric) return;
    hapticToggle();
    if (selectedMetric.isBoolean) {
      updateMetric.mutate({ metricKey: selectedMetric.key, value: booleanValue, isBoolean: true });
    } else {
      let value = parseFloat(inputValue);
      if (isNaN(value)) {
        toast.error(t('notifications.toast.please_enter_a_valid_number'));
        return;
      }

      // Convert from display unit back to base unit if needed
      if (currentDisplayUnit && !selectedMetric.isCustom) {
        value = convertToBase(value, selectedMetric.key, currentDisplayUnit);
      }

      // Clamp rating metrics (mood, self_appearance_rating) to valid DB range 1-10
      if (selectedMetric.key === 'mood' || selectedMetric.key === 'self_appearance_rating') {
        value = Math.max(1, Math.min(10, Math.round(value)));
      }

      updateMetric.mutate({ metricKey: selectedMetric.key, value });
    }
  };

  const handleGoalSubmit = () => {
    if (!selectedMetric) return;
    let value = parseFloat(goalValue);
    if (isNaN(value)) {
      toast.error(t('notifications.toast.please_enter_a_valid_number'));
      return;
    }

    // Convert from display unit back to base unit if needed
    if (currentDisplayUnit && !selectedMetric.isCustom) {
      value = convertToBase(value, selectedMetric.key, currentDisplayUnit);
    }

    // Map metric key to goal key
    const goalKeyMap: Record<string, string> = {
      calories: "calories_goal",
      protein: "protein_goal",
      carbs: "carbs_goal",
      fiber: "fiber_goal",
      miles_ran: "miles_goal",
      gym_time: "gym_time_goal",
      sleep: "sleep_goal",
      water_gallons: "water_goal",
      body_weight: "body_weight_goal",
      body_fat_percentage: "body_fat_goal",
      github_commits: "github_commits_goal",
      healthkit_calories: "healthkit_calories_goal",
      healthkit_exercise_time: "healthkit_exercise_time_goal",
      healthkit_stand_hours: "healthkit_stand_hours_goal",
      healthkit_sleep_time: "healthkit_sleep_goal",
      healthkit_steps: "healthkit_steps_goal",
    };

    // Handle custom metrics (custom_1_value -> custom_1_goal, custom_1 -> custom_1_goal, etc.)
    let goalKey = goalKeyMap[selectedMetric.key];
    if (!goalKey && selectedMetric.key.startsWith("custom_")) {
      // Handle both custom_1_value and custom_1 formats
      if (selectedMetric.key.endsWith("_value")) {
        goalKey = selectedMetric.key.replace("_value", "_goal");
      } else {
        // If it's just custom_1, append _goal
        goalKey = `${selectedMetric.key}_goal`;
      }
    }

    if (goalKey) {
      updateGoal.mutate({ 
        goalKey, 
        value,
        caloriesInverse: goalKey === "calories_goal" ? isCaloriesInverse : undefined
      });
    } else {
      toast.error(t('notifications.toast.metric_doesnt_exist'));
    }
  };

  useEffect(() => {
    if (selectedMetric) {
      if (selectedMetric.isBoolean) {
        setBooleanValue(selectedMetric.currentValue === 1);
      } else {
        // Convert current value to display unit if available
        if (currentDisplayUnit && !selectedMetric.isCustom && selectedMetric.currentValue !== null) {
          const converted = convertFromBase(selectedMetric.currentValue, selectedMetric.key, currentDisplayUnit);
          setInputValue(converted.toString());
        } else {
          setInputValue(selectedMetric.currentValue?.toString() || "");
        }
      }

      // Convert goal value to display unit if available
      if (currentDisplayUnit && !selectedMetric.isCustom) {
        const convertedGoal = convertFromBase(selectedMetric.goal, selectedMetric.key, currentDisplayUnit);
        setGoalValue(convertedGoal.toString());
      } else {
        setGoalValue(selectedMetric.goal?.toString() || "");
      }

      // Initialize calories inverse from user goals when editing calories
      if (selectedMetric.key === "calories" && userGoals) {
        setIsCaloriesInverse((userGoals as any).calories_inverse !== false);
      }

      setIsEditingGoal(false);
    }
  }, [selectedMetric?.key, selectedMetric?.currentValue, selectedMetric?.isBoolean, selectedMetric?.goal, currentDisplayUnit, userGoals]);

  // Update goal value separately when goal changes
  useEffect(() => {
    if (selectedMetric && currentDisplayUnit && !selectedMetric.isCustom) {
      const convertedGoal = convertFromBase(selectedMetric.goal, selectedMetric.key, currentDisplayUnit);
      setGoalValue(convertedGoal.toString());
    } else if (selectedMetric) {
      setGoalValue(selectedMetric.goal?.toString() || "");
    }
  }, [selectedMetric?.goal, currentDisplayUnit]);

  // Detect new metrics and trigger punch animation
  useEffect(() => {
    const currentKeys = new Set(enabledMetrics.map(m => m.key));
    const newKeys = new Set<string>();

    currentKeys.forEach((key: string) => {
      if (!prevMetricKeysRef.current.has(key)) {
        newKeys.add(key);
      }
    });

    if (newKeys.size > 0) {
      setNewMetrics(newKeys);

      setTimeout(() => {
        setNewMetrics(new Set());
      }, 400);
    }

    prevMetricKeysRef.current = currentKeys;
  }, [enabledMetrics.map(m => m.key).join(',')]);

  // Detect value changes and trigger flash animation
  useEffect(() => {
    enabledMetrics.forEach(metric => {
      const prevValue = prevValuesRef.current[metric.key];
      const currentValue = metric.currentValue;

      if (prevValue !== undefined && prevValue !== currentValue) {
        if (currentValue !== null && prevValue !== null) {
          const flashType = currentValue > prevValue ? 'increase' : 'decrease';
          setFlashingMetrics(prev => ({ ...prev, [metric.key]: flashType }));

          setTimeout(() => {
            setFlashingMetrics(prev => {
              const next = { ...prev };
              delete next[metric.key];
              return next;
            });
          }, 600);
        }
      }

      prevValuesRef.current[metric.key] = currentValue;
    });
  }, [enabledMetrics.map(m => m.currentValue).join(',')]);

  return (
    <>
      <TooltipProvider>
        {isMetricsLoading ? (
          <div className={cn(
            "flex items-center justify-center",
            displayMode === 'list' && layout === 'grid' ? "py-8" : "py-4"
          )}>
            <MiniGridLoader className="w-6 h-6 text-muted-foreground" />
          </div>
        ) : (
        <div className={displayMode === 'list' && layout === 'grid'
          ? "space-y-2 w-full quick-metric-buttons-container"
          : layout === 'grid'
            ? `grid grid-cols-4 ${isMobile ? 'gap-5' : 'gap-3'} justify-items-center w-full quick-metric-buttons-container`
            : "flex flex-wrap-reverse justify-end gap-2 items-end w-[520px] max-w-[520px] overflow-visible quick-metric-buttons-container"
        }>
          {enabledMetrics.map((metric) => {
            const progress = metric.isBoolean
              ? (metric.currentValue === 1 ? 100 : 0)
              : (metric.currentValue && metric.goal
                ? Math.min((metric.currentValue / metric.goal) * 100, 100)
                : 0);
            const color = getProgressColor(metric.currentValue, metric.goal, metric.isBoolean, metric.key, (metric as any).isInverse);
            const isFlashing = flashingMetrics[metric.key];
            const isNew = newMetrics.has(metric.key);
            const isPunching = punchingMetrics.has(metric.key);
            const flashClass = isFlashing === 'increase'
              ? 'animate-[flash-green_0.6s_ease-out]'
              : isFlashing === 'decrease'
                ? 'animate-[flash-red_0.6s_ease-out]'
                : '';
            const punchClass = isNew ? 'animate-punch' : '';
            const scaleClass = isPunching ? 'animate-scale-press' : '';
            const shakeClass = isFlashing ? 'animate-shake' : '';

            const localizedName = metric.isCustom ? metric.name : getLocalizedMetricName(metric.key, t, metric.name);
            const tooltipText = metric.isBoolean
              ? `${localizedName}: ${metric.currentValue === 1 ? '✓ ' + t('ui.buttons.yes') : '✗ ' + t('ui.buttons.no')}`
              : `${localizedName}: ${metric.currentValue ?? 0}/${metric.goal}`;

            // List view rendering (for mobile Track page)
            if (displayMode === 'list' && layout === 'grid') {
              // Calculate "X left" or "X over" text for numeric metrics (only for metrics with goals)
              let remainingText = '';
              if (!metric.isBoolean && !metric.isRelative && metric.currentValue !== null && metric.goal && metric.goal > 1) {
                const current = metric.currentValue ?? 0;
                const remaining = metric.goal - current;

                if (remaining > 0) {
                  remainingText = t('ui.text.x_left', { count: Math.round(remaining) });
                } else if (remaining < 0) {
                  remainingText = t('ui.text.x_over', { count: Math.round(Math.abs(remaining)) });
                }
              }

              return (
                <div key={metric.key} className="relative">
                  <button
                    data-metric-key={metric.key}
                    onClick={(e) => {
                      // Check if overlay is currently showing for this metric
                      const isOverlayShowing = showZoneTutorial?.metricKey === metric.key;

                      // Check if this is the first interaction with this metric type
                      const isFirstNumericInteraction = !metric.isBoolean && !hasSeenNumericTutorial;
                      const isFirstBooleanInteraction = metric.isBoolean && !hasSeenBooleanTutorial;

                      // If overlay is not showing, show it and return (don't execute action)
                      if (!isOverlayShowing) {
                        setShowZoneTutorial({
                          metricKey: metric.key,
                          type: metric.isBoolean ? 'boolean' : 'numeric'
                        });

                        // Mark as seen on first interaction
                        if (isFirstBooleanInteraction) {
                          setHasSeenBooleanTutorial(true);
                          localStorage.setItem('list_view_boolean_zone_tutorial_seen', 'true');
                        } else if (isFirstNumericInteraction) {
                          setHasSeenNumericTutorial(true);
                          localStorage.setItem('list_view_numeric_zone_tutorial_seen', 'true');
                        }

                        return; // Don't execute the action, just show overlay
                      }

                      // Overlay is showing, reset the timer by re-setting the state
                      setShowZoneTutorial({
                        metricKey: metric.key,
                        type: metric.isBoolean ? 'boolean' : 'numeric'
                      });

                      // Mark that user has manually updated a metric
                      localStorage.setItem('manual_metric_tutorial_dismissed', 'true');

                      // Get click position relative to button
                      const rect = e.currentTarget.getBoundingClientRect();
                      const clickX = e.clientX - rect.left;
                      const buttonWidth = rect.width;
                      const clickPercentage = clickX / buttonWidth;

                      if (metric.isBoolean) {
                        // Boolean metrics: left 33% opens dialog, middle 33% toggles, right 33% opens camera
                        if (clickPercentage < 0.33) {
                          // Open dialog
                          setSelectedMetric(metric);
                        } else if (clickPercentage < 0.66) {
                          // Toggle the boolean value
                          hapticToggle();
                          const newValue = metric.currentValue === 1 ? 0 : 1;
                          updateMetric.mutate({
                            metricKey: metric.key,
                            value: newValue === 1,
                            isBoolean: true
                          });
                          // Hide overlay immediately so user can see the change
                          setShowZoneTutorial(null);
                        } else {
                          // Open progress pic camera
                          openProgressCamera(metric.key);
                        }
                      } else {
                        // Numerical metrics: left 15% decrements, 15-50% opens dialog, 50-85% camera, 85-100% increments
                        if (clickPercentage < 0.15) {
                          // Decrement by 1
                          hapticTap();
                          const currentVal = metric.currentValue ?? 0;
                          // Rating metrics (mood, self_appearance_rating) have DB CHECK constraint >= 1
                          const minValue = (metric.key === 'mood' || metric.key === 'self_appearance_rating') ? 1 : 0;
                          const newValue = Math.max(minValue, currentVal - 1);
                          if (newValue === currentVal) {
                            // Already at minimum, don't send a redundant update
                            setShowZoneTutorial(null);
                            return;
                          }
                          showFloatingIndicator(metric.key, -1, e.clientX, e.clientY);
                          updateMetric.mutate({
                            metricKey: metric.key,
                            value: newValue
                          });
                          // Hide overlay immediately so user can see the change
                          setShowZoneTutorial(null);
                        } else if (clickPercentage < 0.5) {
                          // Open dialog
                          setSelectedMetric(metric);
                        } else if (clickPercentage < 0.85) {
                          // Open progress pic camera
                          openProgressCamera(metric.key);
                        } else {
                          // Increment by 1
                          hapticTap();
                          const currentVal = metric.currentValue ?? 0;
                          // Rating metrics (mood, self_appearance_rating) have DB CHECK constraint <= 10
                          const maxValue = (metric.key === 'mood' || metric.key === 'self_appearance_rating') ? 10 : Infinity;
                          const newValue = Math.min(maxValue, currentVal + 1);
                          if (newValue === currentVal) {
                            // Already at maximum, don't send a redundant update
                            setShowZoneTutorial(null);
                            return;
                          }
                          showFloatingIndicator(metric.key, 1, e.clientX, e.clientY);
                          updateMetric.mutate({
                            metricKey: metric.key,
                            value: newValue
                          });
                          // Hide overlay immediately so user can see the change
                          setShowZoneTutorial(null);
                        }
                      }
                    }}
                    className={`flex items-center gap-3 p-3 bg-card/60 border border-border/40 rounded-xl shadow-sm hover:shadow-md transition-all w-full ring-1 ring-white/10 ${flashClass} ${punchClass} ${scaleClass}`}
                  >
                    <div className="text-2xl flex-shrink-0">{metric.emoji}</div>
                    <div className="flex-1 min-w-0 relative">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-baseline gap-2 min-w-0">
                          <div className="text-sm font-medium text-left">{localizedName}</div>
                          {remainingText && (
                            <div className="text-xs text-muted-foreground/60 whitespace-nowrap">
                              {remainingText}
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground ml-2 flex-shrink-0 flex items-baseline gap-1">
                          <span className={metric.isBoolean ? 'flex items-center gap-1' : ''}>
                            {metric.isBoolean
                              ? (metric.currentValue === 1
                                ? <><span className="text-green-500">✓</span> {t('ui.buttons.yes')}</>
                                : <><span className="text-red-400">✗</span> {t('ui.buttons.no')}</>)
                              : metric.isRelative
                                ? `${metric.currentValue ?? 0}`
                                : `${metric.currentValue ?? 0}/${metric.goal}`
                            }
                          </span>
                          {!metric.isBoolean && metric.unit && metric.unit.trim() !== '' && !metric.unit.startsWith('/') && !metric.unit.toLowerCase().startsWith('of') && (
                            <span className="text-muted-foreground/60">
                              {metric.unit}
                            </span>
                          )}
                        </div>
                      </div>
                      {!metric.isRelative && (
                        <Progress
                          value={progress}
                          className="h-2 w-full"
                          style={{
                            // @ts-ignore - custom CSS variable
                            '--progress-color': color
                          } as any}
                        />
                      )}
                    </div>
                  </button>

                  {/* Zone Overlay - Shows every time at 30% opacity */}
                  {showZoneTutorial?.metricKey === metric.key && (
                    <div className={`absolute inset-0 rounded-xl overflow-hidden pointer-events-none z-10 ${!hasSeenNumericTutorial || !hasSeenBooleanTutorial ? 'animate-pulse' : ''}`}>
                      {metric.isBoolean ? (
                        <div className="flex h-full">
                          {/* Left 33% - Adjust Zone */}
                          <div className="w-[33%] bg-gray-900 flex flex-col items-center justify-center border-r border-gray-400 gap-1">
                            <svg
                              className="w-6 h-6 text-white drop-shadow-lg"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                            </svg>
                            <span className="text-[10px] text-white font-medium text-center leading-tight">{t('quick_metrics.zone_edit_value')}</span>
                          </div>

                          {/* Middle 33% - Toggle Zone */}
                          <div className="w-[34%] bg-gray-900 flex flex-col items-center justify-center border-r border-gray-400 gap-1">
                            <svg
                              className="w-6 h-6 text-white drop-shadow-lg"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            <span className="text-[10px] text-white font-medium text-center leading-tight">{t('quick_metrics.zone_toggle')}</span>
                          </div>

                          {/* Right 33% - Camera Zone */}
                          <div className="w-[33%] bg-gray-900 flex flex-col items-center justify-center gap-1">
                            <Camera className="w-6 h-6 text-white drop-shadow-lg" />
                            <span className="text-[10px] text-white font-medium text-center leading-tight">{t('quick_metrics.zone_progress_pic')}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex h-full">
                          {/* Left 15% - Decrement Zone */}
                          <div className="w-[15%] bg-gray-900 flex flex-col items-center justify-center border-r border-gray-400 gap-1">
                            <div className="text-lg font-bold text-white drop-shadow-lg">-1</div>
                          </div>

                          {/* 15-50% - Adjust Zone */}
                          <div className="w-[35%] bg-gray-900 flex flex-col items-center justify-center border-r border-gray-400 gap-1">
                            <svg
                              className="w-6 h-6 text-white drop-shadow-lg"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                            </svg>
                            <span className="text-[9px] text-white font-medium text-center leading-tight">{t('quick_metrics.zone_edit_value')}</span>
                          </div>

                          {/* 50-85% - Camera Zone */}
                          <div className="w-[35%] bg-gray-900 flex flex-col items-center justify-center border-r border-gray-400 gap-1">
                            <Camera className="w-6 h-6 text-white drop-shadow-lg" />
                            <span className="text-[9px] text-white font-medium text-center leading-tight">{t('quick_metrics.zone_progress_pic')}</span>
                          </div>

                          {/* Right 15% - Increment Zone */}
                          <div className="w-[15%] bg-gray-900 flex flex-col items-center justify-center gap-1">
                            <div className="text-lg font-bold text-white drop-shadow-lg">+1</div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            }

            // Circle view rendering (default)
            return (
              <Tooltip key={metric.key}>
                <TooltipTrigger asChild>
                  <div className={`relative inline-block w-11 ${shakeClass}`} ref={enabledMetrics.length === 1 ? singleCircleRef : undefined}>
                    <button
                      data-metric-key={metric.key}
                      onClick={() => {
                        setSelectedMetric(metric);
                        // Mark that user has manually updated a metric
                        localStorage.setItem('manual_metric_tutorial_dismissed', 'true');
                      }}
                      draggable={true}
                      onDragStart={(e) => {
                        e.dataTransfer.setData('metric-key', metric.key);
                        e.dataTransfer.setData('metric-name', metric.name);
                        e.dataTransfer.setData('metric-emoji', metric.emoji);
                        e.dataTransfer.effectAllowed = 'copy';
                      }}
                      className="relative group cursor-grab active:cursor-grabbing block"
                    >
                      <div className={`w-11 h-11 rounded-full bg-secondary flex items-center justify-center text-xl shadow-md hover:shadow-lg transition-shadow relative overflow-hidden ring-1 ring-white/10 ${flashClass} ${punchClass}`}>
                        {/* Progress ring */}
                        <svg className="absolute inset-0 w-full h-full -rotate-90" style={{ strokeDasharray: 100, strokeDashoffset: 100 - progress }}>
                          <circle
                            cx="22"
                            cy="22"
                            r="20"
                            fill="none"
                            stroke={color}
                            strokeWidth="3"
                            style={{
                              strokeDasharray: `${2 * Math.PI * 20}`,
                              strokeDashoffset: `${2 * Math.PI * 20 * (1 - progress / 100)}`,
                              transition: 'stroke-dashoffset 0.8s cubic-bezier(0.4, 0, 0.2, 1), stroke 0.3s ease',
                            }}
                          />
                        </svg>
                        <span className="relative z-10">{metric.emoji}</span>
                      </div>
                    </button>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>{tooltipText}</p>
                </TooltipContent>
              </Tooltip>
            );
          })}

          {enabledMetrics.length === 1 && singleTipPos && layout === 'horizontal' && (
            <div className="fixed z-40 pointer-events-none" style={{ top: singleTipPos.top, left: singleTipPos.left + singleTipPos.width / 2, transform: 'translateX(-50%)' }}>
              <div className="animate-[punch_0.3s_ease-out_1s_both]">
                <div className="relative bg-popover text-popover-foreground px-3 py-2 rounded-xl shadow-lg text-xs font-medium whitespace-nowrap border border-border">
                  <span className="animate-shimmer bg-gradient-to-r from-foreground/60 via-foreground to-foreground/60 bg-clip-text text-transparent bg-[length:200%_100%]">{t('quick_metrics.click_to_update')}</span>
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-popover border-r border-b border-border rotate-45" />
                </div>
              </div>
            </div>
          )}

          {/* Loading indicator for pending metric add */}
          {addMetricMutation.isPending && (
            displayMode === 'list' && layout === 'grid' ? (
              <div className="flex items-center gap-3 p-3 rounded-xl shadow-sm w-full border-2 border-dashed border-primary/40 bg-primary/5 animate-pulse">
                <MiniGridLoader className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="text-sm font-medium text-primary/70">{t('quick_metrics.adding_metric')}</span>
              </div>
            ) : (
              <div className="w-11 h-11 rounded-full bg-secondary/50 flex items-center justify-center shadow-md animate-pulse ring-1 ring-white/10">
                <MiniGridLoader className="w-5 h-5 text-primary" />
              </div>
            )
          )}

          {/* Add Metric Button */}
          {isMobile ? (
            // Mobile: Use Dialog for better centered positioning and scrolling
            <>
              <div className="relative">
                <button
                  className="relative group"
                  onClick={() => setShowAddMetric(true)}
                >
                  {displayMode === 'list' && layout === 'grid' ? (
                    <div className="flex items-center gap-3 p-3 rounded-xl shadow-sm hover:shadow-md transition-all w-full border-2 border-dashed border-muted-foreground/40 hover:border-primary/60">
                      <Plus className="w-5 h-5 text-muted-foreground group-hover:text-primary flex-shrink-0" />
                      <span className="text-sm font-medium text-muted-foreground group-hover:text-primary">{t('quick_metrics.add_metric')}</span>
                    </div>
                  ) : (
                    <div className="w-11 h-11 rounded-full flex items-center justify-center text-xl shadow-md hover:shadow-lg transition-all border-2 border-dashed border-muted-foreground/40 hover:border-primary/60">
                      <Plus className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
                    </div>
                  )}
                </button>
                {/* Guiding tip when no metrics - for horizontal layout */}
                {enabledMetrics.length === 0 && layout === 'horizontal' && showNoMetricsTooltip && !hideTutorialTooltips && !mobileBackdropActive && (
                  <div className="absolute -top-12 right-0 animate-bounce opacity-0 animate-[fadeInOnce_0.7s_ease-in_forwards] z-30">
                    <div className="relative bg-popover border border-border text-popover-foreground px-3 py-2 rounded-xl shadow-md text-xs font-medium whitespace-nowrap">
                      {t('quick_metrics.no_metrics_horizontal')}
                      <div className="absolute -bottom-1 right-4 w-3 h-3 bg-popover border-r border-b border-border rotate-45" />
                    </div>
                  </div>
                )}
                {/* Guiding tip when no metrics - for grid layout (mobile Track page) */}
                {enabledMetrics.length === 0 && layout === 'grid' && showNoMetricsTooltip && !hideTutorialTooltips && !mobileBackdropActive && (
                  <div className={cn(
                    "absolute w-48 animate-[bounce-horizontal_1s_ease-in-out_infinite] z-30 opacity-0 animate-[fadeInOnce_0.7s_ease-in_forwards]",
                    displayMode === 'list' ? "-top-14 left-0" : "top-0 -right-52"
                  )}>
                    <div className={cn(
                      "relative bg-popover border border-border text-popover-foreground px-4 py-2.5 rounded-xl shadow-md",
                    )}>
                      <p className="text-xs font-medium text-center leading-snug">
                        {t('quick_metrics.no_metrics_grid')}
                      </p>
                      <div className={cn(
                        "absolute w-4 h-4 bg-popover border-border rotate-45",
                        displayMode === 'list'
                          ? "bottom-0 left-4 translate-y-1/2 border-b border-r"
                          : "top-1/2 -translate-y-1/2 -left-2 border-l border-b"
                      )} />
                    </div>
                  </div>
                )}
              </div>

              <Dialog open={showAddMetric} onOpenChange={setShowAddMetric}>
                <DialogContent className="max-w-[90vw] w-full max-h-[85vh] flex flex-col p-0">
                  <DialogHeader className="p-4 pb-3 border-b">
                    <DialogTitle>{t('quick_metrics.add_metric')}</DialogTitle>
                  </DialogHeader>
                  <div className="overflow-y-auto flex-1 px-2">
                    {/* Create Your Own - Top Section */}
                    <div className="p-2">
                      <Button
                        variant="outline"
                        className="w-full justify-start"
                        onClick={() => {
                          setShowAddMetric(false);
                          setShowCustomMetricsDialog(true);
                        }}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {t('quick_metrics.create_own')}
                      </Button>
                    </div>

                    {/* Divider with text */}
                    <div className="flex items-center gap-3 px-4 py-2">
                      <div className="flex-1 h-px bg-border" />
                      <span className="text-xs text-muted-foreground">{t('quick_metrics.pick_popular')}</span>
                      <div className="flex-1 h-px bg-border" />
                    </div>

                    {getAvailableMetrics().length > 0 ? (
                      <div className="p-2">
                        {getAvailableMetrics().map((metric) => (
                          <button
                            key={metric.key}
                            onClick={() => {
                              addMetricMutation.mutate({ metricKey: metric.key, category: metric.category });
                              setShowAddMetric(false);
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted/50 rounded-md transition-colors text-left"
                          >
                            <span className="text-xl">{metric.emoji}</span>
                            <span className="text-sm font-medium">{getLocalizedMetricName(metric.key, t, metric.name)}</span>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="p-4 text-sm text-muted-foreground text-center">
                        {t('quick_metrics.all_enabled')}
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </>
          ) : (
            // Desktop: Use Popover
            <Popover open={showAddMetric} onOpenChange={setShowAddMetric}>
              <PopoverTrigger asChild>
                <div className="relative">
                  <button className="relative group">
                    <div className="w-11 h-11 rounded-full flex items-center justify-center text-xl shadow-md hover:shadow-lg transition-all border-2 border-dashed border-muted-foreground/40 hover:border-primary/60">
                      <Plus className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
                    </div>
                  </button>
                  {/* Guiding tip when no metrics */}
                  {enabledMetrics.length === 0 && layout === 'horizontal' && (
                    <div className="absolute -top-12 right-0 animate-bounce">
                      <div className="relative bg-popover text-popover-foreground px-3 py-2 rounded-xl shadow-lg text-xs font-medium whitespace-nowrap border border-border">
                        {t('quick_metrics.no_metrics_horizontal')}
                        <div className="absolute -bottom-1 right-4 w-3 h-3 bg-popover border-r border-b border-border rotate-45" />
                      </div>
                    </div>
                  )}
                </div>
              </PopoverTrigger>
              <PopoverContent
                className="w-72 p-0"
                align="center"
                side="top"
                sideOffset={8}
                alignOffset={0}
                collisionPadding={16}
                avoidCollisions={true}
              >
                <div className="p-3 border-b">
                  <h4 className="font-semibold text-sm">{t('quick_metrics.add_metric')}</h4>
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                  {/* Create Your Own - Top Section */}
                  <div className="p-2">
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => {
                        setShowAddMetric(false);
                        setShowCustomMetricsDialog(true);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {t('quick_metrics.create_own')}
                    </Button>
                  </div>

                  {/* Divider with text */}
                  <div className="flex items-center gap-3 px-4 py-2">
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-xs text-muted-foreground">{t('quick_metrics.pick_popular')}</span>
                    <div className="flex-1 h-px bg-border" />
                  </div>

                  {getAvailableMetrics().length > 0 ? (
                    <div className="p-2">
                      {getAvailableMetrics().map((metric) => (
                        <button
                          key={metric.key}
                          onClick={() => addMetricMutation.mutate({ metricKey: metric.key, category: metric.category })}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted/50 rounded-md transition-colors text-left"
                        >
                          <span className="text-xl">{metric.emoji}</span>
                          <span className="text-sm font-medium">{getLocalizedMetricName(metric.key, t, metric.name)}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 text-sm text-muted-foreground text-center">
                      {t('quick_metrics.all_enabled')}
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
        )}

        {/* Manual Metric Tutorial Bubble for Mobile Track Page */}
        {enabledMetrics.length >= 1 && layout === 'grid' && isMobile && showSingleMetricTutorial && !mobileBackdropActive && !showAIBubble && (
          <div className={cn(
            "relative animate-bounce z-30",
            displayMode === 'list' ? "mt-2" : "mt-6"
          )}>
            <div className={cn(
              "relative bg-primary dark:bg-primary text-primary-foreground dark:text-primary-foreground px-4 py-3 rounded-xl shadow-xl max-w-[280px]",
              displayMode === 'list' && "ml-auto"
            )}>
              <p className="text-sm font-medium text-left mb-3">
                {t('quick_metrics.tap_to_update')}
              </p>
              <Button
                variant="secondary"
                size="sm"
                className="w-full"
                onClick={() => {
                  localStorage.setItem('manual_metric_tutorial_dismissed', 'true');
                  setShowSingleMetricTutorial(false);
                }}
              >
                {t('quick_metrics.got_it')}
              </Button>
              <div className={cn(
                "absolute -top-2 w-4 h-4 bg-primary dark:bg-primary rotate-45",
                displayMode === 'list' ? "right-8" : "left-8"
              )} />
            </div>
          </div>
        )}
      </TooltipProvider>

      {/* Custom Metrics Dialog */}
      <CustomMetricsDialog
        userId={userId}
        iconOnly={false}
        open={showCustomMetricsDialog}
        onOpenChange={(open) => setShowCustomMetricsDialog(open)}
      />

      {/* Edit Custom Metric Details Dialog */}
      {selectedMetric?.isCustom && (() => {
        // Find the full custom metric data
        const customMetricData = customMetrics?.find(m => m.metric_key === selectedMetric.key);
        if (!customMetricData) return null;

        return (
          <CustomMetricsDialog
            userId={userId}
            open={showEditDetailsDialog}
            onOpenChange={(open) => setShowEditDetailsDialog(open)}
            editMode={true}
            initialValues={{
              name: customMetricData.metric_name,
              emoji: customMetricData.metric_emoji,
              unit: customMetricData.metric_unit || "",
              goal: selectedMetric.goal?.toString() || "100",
              inverse: customMetricData.is_inverse || false,
              metricType: (customMetricData.metric_type as "numeric" | "boolean") || "numeric",
              isAdditive: customMetricData.is_additive ?? true,
              isRelative: customMetricData.is_relative ?? false,
            }}
            onSaveEdit={(editedValues) => {
              updateCustomMetricDetails.mutate({
                metricKey: selectedMetric.key,
                name: editedValues.name,
                emoji: editedValues.emoji,
                unit: editedValues.unit,
                goal: editedValues.goal,
                inverse: editedValues.inverse,
                metricType: editedValues.metricType,
                isAdditive: editedValues.isAdditive,
                isRelative: editedValues.isRelative,
                previousType: (customMetricData.metric_type as "numeric" | "boolean") || "numeric",
              });
            }}
          />
        );
      })()}

      <Dialog open={!!selectedMetric} onOpenChange={(open) => !open && setSelectedMetric(null)}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md [&>div]:overflow-y-auto [&>div]:scrollbar-none [&>div]:[-webkit-overflow-scrolling:touch]">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg flex items-center gap-2">
              <span>{t('quick_metrics.update_metric', { emoji: selectedMetric?.emoji, name: selectedMetric?.name })}</span>
              {selectedMetric?.key.startsWith('custom_') && (
                <Button variant="ghost" size="sm" onClick={() => setShowEditDetailsDialog(true)} className="h-6 w-6 p-0 hover:bg-transparent">
                  <Pencil className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
                </Button>
              )}
              {selectedMetric && !selectedMetric.isBoolean && !selectedMetric.isCustom && currentDisplayUnit && getAvailableUnits(selectedMetric.key).length > 1 && (
                <Select
                  value={currentDisplayUnit}
                  onValueChange={(value) => {
                    setCurrentDisplayUnit(value as UnitType);
                    setUnitPreference(selectedMetric.key, value as UnitType);
                  }}
                >
                  <SelectTrigger className="w-[80px] h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableUnits(selectedMetric.key).map((unit) => (
                      <SelectItem key={unit} value={unit}>
                        {unit}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 px-2 sm:px-6">
            {selectedMetric?.isBoolean ? (
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="boolean-value"
                    checked={booleanValue}
                    onCheckedChange={(checked) => setBooleanValue(checked === true)}
                  />
                  <Label
                    htmlFor="boolean-value"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {selectedMetric?.name}
                  </Label>
                </div>
              </div>
            ) : (
              <div className="space-y-2 w-full">
                <Label htmlFor="metric-value">
                  {t('quick_metrics.unit', { unit: currentDisplayUnit || selectedMetric?.unit })}
                </Label>
                {selectedMetric?.readOnly ? (
                  <div className="space-y-3 w-full overflow-hidden">
                    <div className="text-sm text-muted-foreground p-3 bg-muted rounded-md break-words">
                      {selectedMetric?.key.startsWith('healthkit_')
                        ? t('quick_metrics.read_only_healthkit')
                        : t('quick_metrics.read_only_github')}
                    </div>
                    {selectedMetric?.key === 'github_commits' && (
                      <div className="p-3 bg-muted/50 rounded-lg space-y-3 w-full overflow-hidden">
                        {githubConnection ? (
                          <div className="space-y-2 w-full overflow-hidden">
                            <p className="text-xs text-muted-foreground break-words">
                              {t('quick_metrics.connected_github')}
                            </p>
                            <div className="w-full overflow-hidden">
                              <GitHubOAuth
                                userId={userId}
                                onSuccess={() => {
                                  queryClient.invalidateQueries({ queryKey: ["github-connection"] });
                                  queryClient.invalidateQueries({ queryKey: ["daily-entry"] });
                                  toast.success(t('quick_metrics.github_account_updated'));
                                }}
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-2 w-full overflow-hidden">
                            <p className="text-xs text-muted-foreground break-words">
                              {t('quick_metrics.connect_github')}
                            </p>
                            <div className="w-full overflow-hidden">
                              <GitHubOAuth
                                userId={userId}
                                onSuccess={() => {
                                  queryClient.invalidateQueries({ queryKey: ["github-connection"] });
                                  queryClient.invalidateQueries({ queryKey: ["daily-entry"] });
                                  toast.success(t('quick_metrics.syncing_github'));
                                  setSelectedMetric(null);
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <Input
                    id="metric-value"
                    type="number"
                    step="0.1"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={t('quick_metrics.enter_value', { name: selectedMetric?.name.toLowerCase() })}
                    className="max-w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSubmit();
                      }
                    }}
                  />
                )}

                {/* Goal editing - not available for mood, appearance (fixed at 10), or relative metrics */}
                {selectedMetric?.key !== 'mood' && selectedMetric?.key !== 'self_appearance_rating' && !selectedMetric?.isRelative && (
                  <>
                    {!isEditingGoal ? (
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">
                          {t('quick_metrics.out_of', {
                            value: currentDisplayUnit && selectedMetric ?
                              formatValueWithUnit(
                                convertFromBase(selectedMetric.goal, selectedMetric.key, currentDisplayUnit),
                                currentDisplayUnit,
                                selectedMetric.key
                              ) :
                              `${selectedMetric?.goal} ${selectedMetric?.unit}`
                          })}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 hover:bg-transparent"
                          onClick={() => setIsEditingGoal(true)}
                        >
                          <Pencil className="w-3 h-3 opacity-50 hover:opacity-100" />
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Label htmlFor="goal-value">
                          {t('quick_metrics.edit_goal', { unit: currentDisplayUnit || selectedMetric?.unit })}
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            id="goal-value"
                            type="number"
                            step="0.1"
                            value={goalValue}
                            onChange={(e) => setGoalValue(e.target.value)}
                            placeholder={t('quick_metrics.enter_goal')}
                            className="max-w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                handleGoalSubmit();
                              }
                            }}
                          />
                          <Button onClick={handleGoalSubmit} disabled={updateGoal.isPending} size="sm">
                            {t('quick_metrics.save')}
                          </Button>
                        </div>
                        {/* Show "Above is bad" checkbox for calories */}
                        {selectedMetric?.key === "calories" && (
                          <div className="flex items-center space-x-2 pt-1">
                            <Checkbox
                              id="calories-inverse-quick"
                              checked={isCaloriesInverse}
                              onCheckedChange={(checked) => setIsCaloriesInverse(checked === true)}
                            />
                            <label htmlFor="calories-inverse-quick" className="text-sm font-normal cursor-pointer text-foreground">
                              {t('ui.text.above_is_bad')}
                            </label>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-2">
              {/* Desktop: Delete button on the left */}
              <div className="hidden sm:flex sm:gap-2 w-auto">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => { if (selectedMetric) { setMetricKeyToDelete(selectedMetric.key); setSelectedMetric(null); } }}
                  disabled={removeMetricMutation.isPending}
                  className="gap-1"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              {/* Action buttons */}
              <div className="flex gap-2 w-full sm:w-auto">
                {/* Mobile: Trash button on the left */}
                <div className="sm:hidden flex gap-1">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => { if (selectedMetric) { setMetricKeyToDelete(selectedMetric.key); setSelectedMetric(null); } }}
                    disabled={removeMetricMutation.isPending}
                    className="h-9 w-9 p-0"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <Button variant="outline" onClick={() => setSelectedMetric(null)} className="flex-1 sm:flex-initial">
                  {t('quick_metrics.cancel')}
                </Button>
                {!selectedMetric?.readOnly && (
                  <Button onClick={handleSubmit} disabled={updateMetric.isPending} className="flex-1 sm:flex-initial">
                    {updateMetric.isPending ? t('quick_metrics.updating') : t('quick_metrics.update')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Referral Credits Celebration Dialog */}
      <ReferralCreditsDialog
        open={referralCreditsData !== null}
        onOpenChange={(open) => !open && setReferralCreditsData(null)}
        creditsAwarded={referralCreditsData?.creditsAwarded || 0}
        referrerUsername={referralCreditsData?.referrerUsername || ''}
        referrerName={referralCreditsData?.referrerName || ''}
      />

      {/* Delete Metric Confirmation Dialog */}
      <AlertDialog open={metricKeyToDelete !== null} onOpenChange={(open) => !open && setMetricKeyToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('quick_metrics.delete_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('quick_metrics.delete_confirm_desc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('quick_metrics.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (metricKeyToDelete) {
                  removeMetricMutation.mutate({ metricKey: metricKeyToDelete });
                }
                setMetricKeyToDelete(null);
              }}
            >
              {t('quick_metrics.delete_confirm_yes')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Floating +1/-1 Indicators */}
      {floatingIndicators.map(indicator => (
        <div
          key={indicator.id}
          className="fixed pointer-events-none z-50 animate-float-up"
          style={{
            left: indicator.x,
            top: indicator.y,
            transform: 'translate(-50%, -50%)'
          }}
        >
          <div className={`text-3xl font-bold ${indicator.value > 0
            ? 'text-green-500 drop-shadow-[0_2px_8px_rgba(34,197,94,0.5)]'
            : 'text-red-500 drop-shadow-[0_2px_8px_rgba(239,68,68,0.5)]'
            }`}>
            {indicator.value > 0 ? '+' : ''}{indicator.value}
          </div>
        </div>
      ))}
    </>
  );
};
